import type { DeliveryStatus } from './BlastDeliveryStatus'
import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DetailPage } from '@/components/templates/detail-page'
import { useViewOnly } from '@/components/templates/app-shell'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { getJSON, patchJSON, postJSON } from '@/lib/api'
import {
  BlastActions,
  BlastStatusBadge,
  CancelRemainderDialog,
  EditPausedContentDialog,
  SendingProgress,
} from '@/pages/BlastReportControls'

type RecipientFilter =
  | 'skipped'
  | 'sent'
  | 'all'
  | 'delivered'
  | 'clicked'
  | 'unsubscribed'
  | 'failed'

interface ReportRecipient {
  contact_id: number
  name: string
  email: string
  provider_status: string
  delivery_status: DeliveryStatus
  clicked: boolean
  unsubscribed?: boolean
  content_version: string
}

interface BlastReportResponse {
  blast: {
    id: number
    subject: string
    body: string
    content_version: number
    activity: { kind: 'paused' | 'content_changed' | 'resumed'; occurred_at: string }[]
    status: 'draft' | 'scheduled' | 'sending' | 'paused' | 'sent' | 'canceled'
    sent_at: string | null
    sent_count: number
    pending_count: number
    canceled_count: number
    report: {
      audience_label: string
      from: string
      sent_at_label: string | null
      totals: {
        sent: number
        skipped?: number
        delivered: number
        clicked: number
        unsubscribed: number
        bounced: number
        complaints: number
      }
      links: { url: string; unique_clickers: number; total_clicks: number }[]
      recipients: ReportRecipient[]
    }
  }
}

export function BlastReportPage({ blastId, onBack }: { blastId: number; onBack: () => void }) {
  const viewOnly = useViewOnly()
  const query = useQuery({
    queryKey: ['blast-report', blastId],
    queryFn: () => getJSON<BlastReportResponse>(`/api/v1/blasts/${blastId}`),
    retry: false,
  })
  const [tab, setTab] = React.useState('links')
  const [recipientFilter, setRecipientFilter] = React.useState<RecipientFilter>('all')
  const [confirmCancel, setConfirmCancel] = React.useState(false)
  const [editingContent, setEditingContent] = React.useState(false)
  const queryClient = useQueryClient()
  const pause = useMutation({
    mutationFn: () => postJSON(`/api/v1/blasts/${blastId}/pause`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['blast-report', blastId] }),
  })
  const cancel = useMutation({
    mutationFn: () => postJSON(`/api/v1/blasts/${blastId}/cancel_remainder`, {}),
    onSuccess: () => {
      setConfirmCancel(false)
      void queryClient.invalidateQueries({ queryKey: ['blast-report', blastId] })
    },
  })
  const resume = useMutation({
    mutationFn: () => postJSON(`/api/v1/blasts/${blastId}/resume`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['blast-report', blastId] }),
  })
  const editContent = useMutation({
    mutationFn: (content: { subject: string; body: string }) =>
      patchJSON(`/api/v1/blasts/${blastId}`, { blast: content }),
    onSuccess: () => {
      setEditingContent(false)
      void queryClient.invalidateQueries({ queryKey: ['blast-report', blastId] })
    },
  })

  if (query.isLoading) return <EmptyState title="Loading Blast report" className="min-h-52" />
  if (query.isError || !query.data)
    return <EmptyState title="Blast report unavailable" className="min-h-52" />

  const { blast } = query.data
  const report = blast.report
  const recipients = filterRecipients(report.recipients, recipientFilter)
  const drill = (filter: RecipientFilter) => {
    setRecipientFilter(filter)
    setTab('recipients')
  }

  return (
    <DetailPage
      title={blast.subject}
      subtitle={`To ${report.audience_label} · from ${report.from}`}
      badge={<BlastStatusBadge status={blast.status} sentAtLabel={report.sent_at_label} />}
      backHref="/communication/blasts"
      backLabel="Blasts"
      onBack={onBack}
      actions={
        <BlastActions
          viewOnly={viewOnly}
          status={blast.status}
          pausePending={pause.isPending}
          resumePending={resume.isPending}
          pendingCount={blast.pending_count}
          onPause={() => pause.mutate()}
          onCancel={() => setConfirmCancel(true)}
          onEdit={() => setEditingContent(true)}
          onResume={() => resume.mutate()}
        />
      }
    >
      <div className="flex flex-col gap-6">
        {['sending', 'paused', 'canceled'].includes(blast.status) && (
          <SendingProgress
            status={blast.status}
            sentCount={blast.sent_count}
            pendingCount={blast.pending_count}
            canceledCount={blast.canceled_count}
          />
        )}
        <ReportTotals totals={report.totals} onDrill={drill} />

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="links">Link clicks</TabsTrigger>
            <TabsTrigger value="recipients">Recipients</TabsTrigger>
          </TabsList>
          <TabsContent value="links" className="mt-4">
            <LinkClicks links={report.links} />
          </TabsContent>
          <TabsContent value="recipients" className="mt-4">
            <RecipientTable recipients={recipients} filter={recipientFilter} />
          </TabsContent>
        </Tabs>
      </div>
      <CancelRemainderDialog
        open={confirmCancel}
        pendingCount={blast.pending_count}
        pending={cancel.isPending}
        error={cancel.isError}
        onOpenChange={setConfirmCancel}
        onConfirm={() => cancel.mutate()}
      />
      <EditPausedContentDialog
        open={editingContent}
        subject={blast.subject}
        body={blast.body}
        pending={editContent.isPending}
        error={editContent.isError}
        onOpenChange={setEditingContent}
        onSave={(content) => editContent.mutate(content)}
      />
    </DetailPage>
  )
}

function ReportTotals({
  totals,
  onDrill,
}: {
  totals: BlastReportResponse['blast']['report']['totals']
  onDrill: (filter: RecipientFilter) => void
}) {
  return (
    <Card>
      <CardContent
        className={`grid grid-cols-2 divide-x divide-border p-0 sm:grid-cols-3 ${(totals.skipped ?? 0) > 0 ? 'lg:grid-cols-6' : 'lg:grid-cols-5'}`}
      >
        <ReportTotal
          label="Sent"
          value={totals.sent}
          onClick={() => onDrill('sent')}
          ariaLabel={`Show all ${totals.sent} sent recipients`}
          totalKey="sent"
        />
        <ReportTotal
          label="Delivered"
          value={totals.delivered}
          onClick={() => onDrill('delivered')}
          ariaLabel={`Show ${totals.delivered} delivered recipients`}
          totalKey="delivered"
        />
        {(totals.skipped ?? 0) > 0 && (
          <ReportTotal
            label="Skipped"
            value={totals.skipped}
            onClick={() => onDrill('skipped')}
            ariaLabel={`Show ${totals.skipped} skipped recipients`}
            totalKey="skipped"
          />
        )}
        <ReportTotal
          label="Clicked"
          value={totals.clicked}
          onClick={() => onDrill('clicked')}
          ariaLabel={`Show ${totals.clicked} clicked recipients`}
          totalKey="clicked"
        />
        <ReportTotal
          label="Unsubscribed"
          value={totals.unsubscribed}
          onClick={() => onDrill('unsubscribed')}
          ariaLabel={`Show ${totals.unsubscribed} unsubscribed recipient`}
          totalKey="unsubscribed"
        />
        <ReportTotal
          label="Bounced / complaints"
          value={`${totals.bounced} / ${totals.complaints}`}
          onClick={() => onDrill('failed')}
          ariaLabel={`Show ${totals.bounced} bounced recipients and ${totals.complaints} complaints`}
          totalKey="failed"
        />
      </CardContent>
    </Card>
  )
}

function ReportTotal({
  label,
  value,
  onClick,
  ariaLabel,
  totalKey,
}: {
  label: string
  value: React.ReactNode
  onClick: () => void
  ariaLabel: string
  totalKey: string
}) {
  return (
    <button
      type="button"
      className="px-4 py-3 text-left transition-colors hover:bg-muted/50"
      onClick={onClick}
      aria-label={ariaLabel}
    >
      <span className="block text-caption uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span
        className="mt-1 block text-2xl font-normal tracking-tight text-foreground"
        data-report-total={totalKey}
      >
        {value}
      </span>
    </button>
  )
}

function LinkClicks({ links }: { links: BlastReportResponse['blast']['report']['links'] }) {
  if (links.length === 0)
    return (
      <Card>
        <EmptyState title="No link clicks" className="min-h-52" />
      </Card>
    )
  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="h-8 pl-4">Link</TableHead>
              <TableHead className="h-8 text-right">Unique clickers</TableHead>
              <TableHead className="h-8 pr-4 text-right">Total clicks</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {links.map((link) => (
              <TableRow key={link.url}>
                <TableCell className="py-2 pl-4 text-body-sm text-foreground">{link.url}</TableCell>
                <TableCell className="py-2 text-right text-body-sm text-foreground">
                  {link.unique_clickers}
                </TableCell>
                <TableCell
                  className="py-2 pr-4 text-right text-body-sm text-muted-foreground"
                  data-link-total
                >
                  {link.total_clicks}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}

function RecipientTable({
  recipients,
  filter,
}: {
  recipients: ReportRecipient[]
  filter: RecipientFilter
}) {
  if (recipients.length === 0)
    return (
      <Card>
        <EmptyState title="No matching recipients" className="min-h-52" />
      </Card>
    )
  return (
    <Card>
      <CardContent className="p-0">
        <p className="border-b border-border px-4 py-2 text-caption text-muted-foreground">
          Resolved at send{filter === 'all' ? '' : ` · ${filter}`}
        </p>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="h-8 pl-4">Name</TableHead>
              <TableHead className="h-8">Email</TableHead>
              <TableHead className="h-8">Status</TableHead>
              <TableHead className="h-8 pr-4">Version</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {recipients.map((recipient) => (
              <TableRow key={recipient.contact_id}>
                <TableCell className="py-2 pl-4 text-body-sm text-foreground">
                  {recipient.name}
                </TableCell>
                <TableCell className="py-2 text-body-sm text-muted-foreground">
                  {recipient.email}
                </TableCell>
                <TableCell className="py-2 text-body-sm">
                  <Badge variant={statusVariant(recipient.delivery_status)}>
                    {statusLabel(recipient.delivery_status)}
                  </Badge>
                </TableCell>
                <TableCell className="py-2 pr-4 text-body-sm text-muted-foreground">
                  Version {recipient.content_version}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}

function filterRecipients(recipients: ReportRecipient[], filter: RecipientFilter) {
  if (filter === 'all') return recipients
  if (filter === 'sent')
    return recipients.filter((recipient) => recipient.provider_status !== 'skipped')
  if (filter === 'delivered')
    return recipients.filter((recipient) => recipient.provider_status === 'delivered')
  if (filter === 'failed')
    return recipients.filter((recipient) =>
      ['bounced', 'complained'].includes(recipient.delivery_status),
    )
  if (filter === 'unsubscribed')
    return recipients.filter(
      (recipient) => recipient.unsubscribed ?? recipient.delivery_status === 'unsubscribed',
    )
  if (filter === 'clicked') return recipients.filter((recipient) => recipient.clicked)
  return recipients.filter((recipient) => recipient.delivery_status === filter)
}

function statusVariant(status: DeliveryStatus): 'neutral' | 'success' | 'warning' | 'destructive' {
  if (status === 'clicked') return 'success'
  if (status === 'unsubscribed') return 'warning'
  if (status === 'bounced' || status === 'complained') return 'destructive'
  return 'neutral'
}

function statusLabel(status: DeliveryStatus) {
  return status === 'complained' ? 'Complaint' : status.charAt(0).toUpperCase() + status.slice(1)
}
